import {isNotNil} from 'ramda-adjunct';

import {isGridDataLoading, selectDatagridRows, type GridDataChange} from 'features/eag-grid';

import {LABOUR_TYPE} from '../constants/labourType';
import {MATERIAL_TYPE} from '../constants/materialType';
import {getDirectSaleItemRowIdsByType} from '../utils/getDirectSaleItemRowIdsByType';

/**
 * Bulk direct-sale item selection by type (T20-138676): classifies the grid's loaded
 * rows (tracked by the host via onGridDataChange) into labour/material by the `type`
 * cell and replaces the grid selection with the matching rows through the external
 * selection channel. The grid's own selection-driven bulk actions then apply.
 *
 * Selection uses row ids (never a predicate) and a zero-match request is guarded here
 * and never dispatched — an empty `{rowIds: []}` 'set' is the channel's EXPLICIT CLEAR
 * and would wipe the current selection.
 *
 * `isAvailable` gates the UI on the EagGrid engine actually reporting data: the legacy
 * grid neither calls onGridDataChange nor listens to selectDatagridRows. Deriving it
 * from the payload (instead of replicating DmsDataGrid's global-flag + per-grid eagFlag
 * decision) cannot drift from the engine that is really rendered.
 */
export const useDirectSaleItemsBulkSelection = (
  gridCode: string,
  gridData: GridDataChange | null
) => {
  const labourRowIds = getDirectSaleItemRowIdsByType(gridData?.rows ?? [], LABOUR_TYPE);
  const materialRowIds = getDirectSaleItemRowIdsByType(gridData?.rows ?? [], MATERIAL_TYPE);

  const selectRowIds = (rowIds: string[]) => {
    if (rowIds.length === 0) {
      return;
    }
    selectDatagridRows(gridCode, {rowIds});
  };

  return {
    isAvailable: isNotNil(gridData),
    isLoading: isGridDataLoading(gridData),
    labourCount: labourRowIds.length,
    materialCount: materialRowIds.length,
    selectLabour: () => selectRowIds(labourRowIds),
    selectMaterial: () => selectRowIds(materialRowIds),
  };
};
